<template>
  <v-dialog
    :value="value"
    :max-width="maxWidth"
    overlay-color="var(--ds-color-reverse)"
    @input="closeDialog"
  >
    <v-card>
      <v-toolbar>
        <v-toolbar-title>
          {{ filterTitleLabel }}
        </v-toolbar-title>
        <v-spacer />
        <v-toolbar-items class="align-items-center">
          <span>
            <StIcon
              class="close-icon"
              icon-name="close"
              @click="closeDialog"
            />
          </span>
        </v-toolbar-items>
      </v-toolbar>

      <v-row
        no-gutters
        class="mt-5 mx-5"
      >
        <GlobalFilterSummary
          v-if="showGlobalFilterFeature"
          :global-filter-by-data-type="globalFilterByDataTypeToShow"
          :data-type="card.dataType"
        />
      </v-row>

      <v-row
        no-gutters
        class="mt-5 mx-5"
      >
        <v-col
          cols="12"
          md="4"
          class="pr-4"
          @scroll.stop
        >
          <PageFilter
            v-model="pagesSelected"
            :pages-items="pagesFormatted"
            :clearable="displayClearable()"
            @clearInfo="trackEvent"
            @input="changeFilterValue()"
          />
        </v-col>
      </v-row>

      <v-row
        no-gutters
        class="pb-5 px-5"
      >
        <v-spacer />
        <v-spacer />
        <v-btn
          v-if="showClearChanges"
          outlined
          rounded
          class="mr-3"
          @click="clearChangesFilter"
        >
          {{ clearChangeLabel }}
        </v-btn>
        <v-btn
          v-if="showClearFilter"
          outlined
          rounded
          class="mr-3"
          @click="clearFilter"
        >
          {{ clearFilterLabel }}
        </v-btn>
        <v-btn
          outlined
          rounded
          @click="applyFilter"
        >
          {{ applyLabel }}
        </v-btn>
      </v-row>
    </v-card>
  </v-dialog>
</template>

<script>
import StIcon from '@stilingue/st-icon';
import PageFilter from './PageFilter';
import GlobalFilterSummary from '@/components/GlobalFilterSummary';
import { trackEventWithUserProperties } from '@/utils/mixpanel';
import FiltersModel from '@/mixins/filters-model';
import pagesModel from '@/mixins/pages-model';

import { Flags } from '@/plugins/rollout';
import { isEqual, cloneDeep, differenceWith } from 'lodash';
import { mapGetters } from 'vuex';
import { DATA_TYPE } from '@/store/modules/cards/constants';

export default {
  name: 'FilterPageMetricsModal',
  components: {
    StIcon,
    PageFilter,
    GlobalFilterSummary,
  },
  mixins: [
    FiltersModel,
    pagesModel,
  ],
  props: {
    value: {
      type: Boolean,
      default: true,
    },
    card: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      appliedFilter: {},
      tempFilter: {},
      channels: [],
      globalFilterByDataType: {},
      globalFilterByDataTypeToShow: {},
      changeOnPageFilter: false,
    };
  },
  computed: {
    filterLabel() {
      return this.$t('components.filter.ModalFilters.label.page.title');
    },
    applyLabel() {
      return this.$t('components.filter.ModalFilters.label.button.applyLabel');
    },
    clearFilterLabel() {
      return this.$t('components.filter.ModalFilters.label.button.clearFilter');
    },
    clearChangeLabel() {
      return this.$t('components.filter.ModalFilters.label.button.clearchange');
    },
    ...mapGetters('dashboard', [
      'activeDashboard',
    ]),
    ...mapGetters('featureFlag', [
      'isEnabled',
    ]),
    ...mapGetters('user', [
      'isEditor',
    ]),
    ...mapGetters('filter', [
      'filterByDataType',
    ]),
    showGlobalFilterFeature() {
      return this.isEnabled(Flags.showGlobalFilter) && this.isEditor;
    },
    filterTitleLabel() {
      return `${this.filterLabel} - ${this.card.props.title}`;
    },
    showClearChanges() {
      return !(isEqual(this.appliedFilter, this.tempFilter)) || this.changeOnPageFilter;
    },
    showClearFilter() {
      return Object.keys(this.appliedFilter).length;
    },
    pagesSelected: {
      get() {
        return this.tempFilter.pages || {};
      },
      set(newValue) {
        const newPages = newValue?.value || [];
        if (!newPages && !newPages?.length) {
          delete this.tempFilter.channels;
          delete this.tempFilter.pages;
        }

        const pageChannels = [];
        newPages.forEach(newPageId => {
          const foundPage = this.pages.find(page => page.id === newPageId);
          if (foundPage) {
            pageChannels.push(foundPage.channel);
          }
        });

        this.channels = Array.from(new Set(pageChannels));
        this.tempFilter.channels = {
          value: this.channels,
        };

        const ids = Array.from(new Set(newPages));
        this.tempFilter.pages = {
          value: ids,
        };

        return true;
      },
    },
  },
  created() {
    this.globalFilterByDataType = this.filterByDataType[this.card.dataType];
    this.globalFilterByDataTypeToShow = this.globalFilterByDataType;

    if ('filter' in this.card.props) {
      this.appliedFilter = cloneDeep(this.card.props.filter);
      this.tempFilter = cloneDeep(this.card.props.filter);
      this.channels = this.tempFilter.channels?.value || [];

      const keysToUse = differenceWith(Object.keys(this.globalFilterByDataType), Object.keys(this.tempFilter), isEqual);

      this.globalFilterByDataTypeToShow = Object.fromEntries(Object.entries(this.globalFilterByDataType).filter(global => keysToUse.includes(global[0])));
    }
  },
  methods: {
    closeDialog() {
      this.$emit('input', false);
    },
    clearFilter() {
      this.$emit('applyFilter', {});
      this.closeDialog();
    },
    applyFilter() {
      // Sanatize before send
      for (const filter in this.tempFilter) {
        if (!this.tempFilter[filter] || (!this.tempFilter[filter]?.value && this.tempFilter[filter]?.value?.length)) {
          delete this.tempFilter[filter];
        }
      }

      this.$emit('applyFilter', this.tempFilter);
      this.closeDialog();
    },
    clearChangesFilter() {
      this.tempFilter = cloneDeep(this.appliedFilter);
    },
    generateObjectToFilter(value) {
      return {
        text: value,
        value,
      };
    },
    displayClearable() {
      return Boolean(this.showGlobalFilterFeature && this.globalFilterByDataType?.pages);
    },
    trackEvent(label) {
      trackEventWithUserProperties('ST Card Filter Delete', { Field: label, 'Data Type': DATA_TYPE.PAGE });
    },
    changeFilterValue() {
      this.changeOnPageFilter = true;
    },
  },
};
</script>

<style scoped lang="scss">
  .align-items-center {
    align-items: center;
  }
</style>
